import {
  add,
  dot,
  obbCorners,
  obbIntersects,
  scale,
  sub,
  type Obb2,
  type Vec2,
} from '@indusketch/geometry';
import {
  DEFAULT_DOCK,
  DOCK_MAX_RAMP_SLOPE,
  DOCK_MIN_BAY_LENGTH,
  DOCK_MIN_BAY_WIDTH,
} from '../catalog.js';
import { onSite, siteRegions } from '../site.js';
import { formatNumber } from '../units.js';
import { fillText, modelTexts } from '../texts.js';
import type { Dock, Opening, ProjectDocument, Wall } from '../types.js';
import { wallFrame } from './walls.js';

export const dockOf = (o: Opening): Dock | null =>
  o.type === 'loading-dock' ? (o.dock ?? DEFAULT_DOCK) : null;

/** Ramp slope as ratio (0.1 = 10 %); null without ramp. */
export const dockRampSlope = (d: Dock): number | null =>
  d.rampLength > 0 ? d.pitDepth / d.rampLength : null;

export interface DockGeometry {
  /** False when the gate shares the approach of its neighbour (double dock). */
  readonly approach: boolean;
  /** Level standing area in the pit, from the outer wall face outwards; empty without approach. */
  readonly pit: Vec2[];
  /** Sloped part, from the end of the pit back up to ground level; empty without approach. */
  readonly ramp: Vec2[];
  /** Leveler plate in the hall floor, from the inner wall face inwards. */
  readonly leveler: Vec2[];
  /** Pit plus ramp as one rectangle (for overlap and site checks). */
  readonly footprint: Obb2;
  /** Unit vector pointing away from the hall. */
  readonly outward: Vec2;
  /** Unit vector along the wall. */
  readonly along: Vec2;
  /** Opening center on the wall centerline. */
  readonly center: Vec2;
  readonly outerFace: number;
  readonly innerFace: number;
}

/**
 * The exterior is to the right of the wall direction (hall walls run counter-clockwise), the
 * same convention the plan symbols use.
 */
export function dockGeometry(wall: Wall, o: Opening, d: Dock): DockGeometry {
  const f = wallFrame(wall);
  const outward = scale(f.normal, -1);
  const along = f.dir;
  const center = add(wall.start, scale(along, o.offsetFromWallStart + o.width / 2));
  const pitCenter = add(center, scale(along, d.approachOffset));
  const t = wall.thickness / 2;
  const rect = (c: Vec2, halfWidth: number, from: number, to: number): Vec2[] => [
    add(add(c, scale(along, -halfWidth)), scale(outward, from)),
    add(add(c, scale(along, halfWidth)), scale(outward, from)),
    add(add(c, scale(along, halfWidth)), scale(outward, to)),
    add(add(c, scale(along, -halfWidth)), scale(outward, to)),
  ];
  const total = d.bayLength + d.rampLength;
  return {
    approach: d.approach,
    pit: d.approach ? rect(pitCenter, d.bayWidth / 2, t, t + d.bayLength) : [],
    ramp: d.approach ? rect(pitCenter, d.bayWidth / 2, t + d.bayLength, t + total) : [],
    leveler: rect(center, d.levelerWidth / 2, -t, -t - d.levelerLength),
    footprint: {
      center: add(pitCenter, scale(outward, t + total / 2)),
      width: d.bayWidth,
      depth: total,
      rotation: Math.atan2(along.y, along.x),
    },
    outward,
    along,
    center,
    outerFace: t,
    innerFace: -t,
  };
}

/** Fall protection only where the trough is at least this deep. */
export const DOCK_RAILING_MIN_DROP = 0.5;
/** Railing line beside the trough edge (on the warning stripe). */
const DOCK_RAILING_INSET = 0.125;

/**
 * Railing lines along the trough of a dock with its own approach, on the enabled sides. Left
 * and right as seen from the yard onto the gates (left = towards the wall's start).
 */
export function dockRailings(wall: Wall, o: Opening, d: Dock): { a: Vec2; b: Vec2 }[] {
  if (!d.approach || d.pitDepth < DOCK_RAILING_MIN_DROP) return [];
  const g = dockGeometry(wall, o, d);
  const pitCenter = add(g.center, scale(g.along, d.approachOffset));
  const at = (a: number, out: number) =>
    add(add(pitCenter, scale(g.along, a)), scale(g.outward, out));
  const bayEnd = g.outerFace + d.bayLength;
  const end =
    d.rampLength > 0 ? bayEnd + d.rampLength * (1 - DOCK_RAILING_MIN_DROP / d.pitDepth) : bayEnd;
  const start = g.outerFace + 0.2;
  const side = d.bayWidth / 2 + DOCK_RAILING_INSET;
  const out: { a: Vec2; b: Vec2 }[] = [];
  if (d.railingLeft) out.push({ a: at(-side, start), b: at(-side, end) });
  if (d.railingRight) out.push({ a: at(side, start), b: at(side, end) });
  return out;
}

/**
 * Gates sharing one trough: the gate holding the approach (`lead`) and the gates without their
 * own approach standing in front of it, sorted along the wall. `openings` are those of the wall.
 */
export function dockGroup(
  wall: Wall,
  openings: readonly Opening[],
  o: Opening,
): { lead: Opening; gates: Opening[] } | null {
  const docks = openings.filter((x) => x.wallId === wall.id && dockOf(x) !== null);
  const centerOf = (x: Opening) => x.offsetFromWallStart + x.width / 2;
  const span = (lead: Opening) => {
    const d = dockOf(lead) ?? DEFAULT_DOCK;
    const mid = centerOf(lead) + d.approachOffset;
    return [mid - d.bayWidth / 2, mid + d.bayWidth / 2] as const;
  };
  const isLead = (x: Opening) => dockOf(x)?.approach === true;
  const lead = isLead(o)
    ? o
    : docks.find((x) => {
        if (!isLead(x)) return false;
        const [a, b] = span(x);
        return centerOf(o) >= a - 1e-6 && centerOf(o) <= b + 1e-6;
      });
  if (!lead) return null;
  const [a, b] = span(lead);
  const gates = docks
    .filter(
      (x) => x.id === lead.id || (!isLead(x) && centerOf(x) >= a - 1e-6 && centerOf(x) <= b + 1e-6),
    )
    .sort((p, q) => p.offsetFromWallStart - q.offsetFromWallStart);
  return { lead, gates };
}

export interface DockInfo {
  readonly opening: Opening;
  readonly wall: Wall;
  readonly dock: Dock;
  readonly geometry: DockGeometry;
}

/** Docks with their own depressed approach (the second gate of a double dock has none). */
export const docksWithApproach = (doc: Pick<ProjectDocument, 'openings' | 'walls'>): DockInfo[] =>
  docksOf(doc).filter((d) => d.dock.approach);

export function docksOf(doc: Pick<ProjectDocument, 'openings' | 'walls'>): DockInfo[] {
  const out: DockInfo[] = [];
  for (const o of Object.values(doc.openings)) {
    const dock = dockOf(o);
    const wall = doc.walls[o.wallId];
    if (dock && wall) out.push({ opening: o, wall, dock, geometry: dockGeometry(wall, o, dock) });
  }
  return out;
}

/** Per walls map, then per openings map: both change identity whenever a dock can move. */
const approachCache = new WeakMap<object, WeakMap<object, DockInfo[]>>();

/**
 * Ground height at `p` outside the halls: 0 on the site, −pit depth in the level part of a
 * depressed dock approach, rising linearly along its ramp. The lowest approach wins.
 */
export function groundElevation(doc: Pick<ProjectDocument, 'openings' | 'walls'>, p: Vec2): number {
  let byOpenings = approachCache.get(doc.walls);
  if (!byOpenings) {
    byOpenings = new WeakMap();
    approachCache.set(doc.walls, byOpenings);
  }
  let docks = byOpenings.get(doc.openings);
  if (!docks) {
    docks = docksWithApproach(doc);
    byOpenings.set(doc.openings, docks);
  }
  let h = 0;
  for (const { dock: d, geometry: g } of docks) {
    const rel = sub(p, add(g.center, scale(g.along, d.approachOffset)));
    if (Math.abs(dot(rel, g.along)) > d.bayWidth / 2) continue;
    const out = dot(rel, g.outward) - g.outerFace;
    if (out < 0 || out > d.bayLength + d.rampLength) continue;
    const z =
      out <= d.bayLength ? -d.pitDepth : -d.pitDepth * (1 - (out - d.bayLength) / d.rampLength);
    h = Math.min(h, z);
  }
  return h;
}

export interface DockWarning {
  readonly openingIds: readonly string[];
  readonly message: string;
}

/** Planning checks for loading docks; purely geometric guidance, no code compliance. */
export function dockWarnings(doc: ProjectDocument): DockWarning[] {
  const docks = docksWithApproach(doc);
  const out: DockWarning[] = [];
  const regions = siteRegions(doc);
  const fmt = (m: number) => formatNumber(m, 2);
  const t = modelTexts().dock;
  for (const { opening: o, dock: d, geometry: g } of docks) {
    const slope = dockRampSlope(d);
    if (slope !== null && slope > DOCK_MAX_RAMP_SLOPE + 1e-9)
      out.push({ openingIds: [o.id], message: fillText(t.steep, { pct: fmt(slope * 100) }) });
    if (d.bayWidth < DOCK_MIN_BAY_WIDTH - 1e-9)
      out.push({
        openingIds: [o.id],
        message: fillText(t.narrow, { width: fmt(d.bayWidth), min: DOCK_MIN_BAY_WIDTH }),
      });
    if (d.bayLength < DOCK_MIN_BAY_LENGTH - 1e-9)
      out.push({
        openingIds: [o.id],
        message: fillText(t.short, { length: fmt(d.bayLength), min: DOCK_MIN_BAY_LENGTH }),
      });
    if (d.bayWidth < o.width - 1e-9) out.push({ openingIds: [o.id], message: t.narrowerThanGate });
    if (d.levelerWidth > o.width + 1e-9) out.push({ openingIds: [o.id], message: t.levelerWider });
    if (regions.length > 0 && !onSite(regions, obbCorners(g.footprint)))
      out.push({ openingIds: [o.id], message: t.offSite });
  }
  for (let i = 0; i < docks.length; i++) {
    for (let j = i + 1; j < docks.length; j++) {
      const a = docks[i];
      const b = docks[j];
      if (a && b && obbIntersects(a.geometry.footprint, b.geometry.footprint))
        out.push({
          openingIds: [a.opening.id, b.opening.id],
          message: t.overlap,
        });
    }
  }
  return out;
}
